import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AuthApiError } from '../account/authApi';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { capture } from '../lib/analytics';
import { GalleryCoverArt } from './GalleryCoverArt';
import { RemixButton } from './RemixButton';
import { ReportDialog } from './ReportDialog';
import { useGalleryEntry, useUpdateMyDescription, useWithdrawSubmission } from './useGallery';
import type { GalleryEntry } from './galleryApi';
import './GalleryDetailPage.css';

/**
 * Detail page for a single approved gallery submission.
 *
 * The card on the listing page is a poster — short, scannable. The
 * detail page is the storefront — it answers: what is this, what
 * does it classify, who can do something with it, and how?
 *
 * Approved submissions are public, so this page does not require
 * auth. Reporting requires a session (the dialog enforces that).
 */
export function GalleryDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { data: entry, isPending, error } = useGalleryEntry(id);
  const [reporting, setReporting] = useState(false);

  if (isPending) {
    return (
      <PageShell>
        <p className="bl-gallery-detail__notice" aria-live="polite">
          Loading…
        </p>
      </PageShell>
    );
  }

  if (error) {
    const status = error instanceof AuthApiError ? error.status : null;
    return (
      <PageShell>
        <p className="bl-gallery-detail__notice bl-gallery-detail__notice--error" role="alert">
          {status === 404
            ? "Couldn't find this entry. It might have been taken down."
            : error.message}
        </p>
        <p>
          <Link to="/gallery">← Back to the gallery</Link>
        </p>
      </PageShell>
    );
  }

  if (!entry) {
    return (
      <PageShell>
        <p className="bl-gallery-detail__notice">No data.</p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <article className="bl-gallery-detail__card">
        <div className="bl-gallery-detail__cover-wrap">
          <GalleryCoverArt
            name={entry.project_name}
            kind={entry.project_kind}
            labels={entry.class_labels}
          />
        </div>

        <div className="bl-gallery-detail__body">
          <p className="bl-gallery-detail__kind">{entry.project_kind} · model</p>
          <h1 className="bl-gallery-detail__title">{entry.project_name}</h1>
          <DescriptionPanel entry={entry} />

          <section
            className="bl-gallery-detail__classes"
            aria-labelledby="bl-detail-classes-heading"
          >
            <h2 id="bl-detail-classes-heading">What it classifies</h2>
            {entry.class_labels.length === 0 ? (
              <p className="bl-gallery-detail__classes-empty">No labels published.</p>
            ) : (
              <ul className="bl-gallery-detail__chips">
                {entry.class_labels.map((label) => (
                  <li key={label} className="bl-gallery-detail__chip">
                    {label}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bl-gallery-detail__try" aria-labelledby="bl-detail-try-heading">
            <h2 id="bl-detail-try-heading">Try it</h2>
            <p>
              Tap <strong>Remix this</strong> to fork the project into your studio. You start with
              the same labels and add your own samples — the maker&rsquo;s training data stays
              private.
            </p>
            <div className="bl-gallery-detail__actions">
              <RemixButton submissionId={entry.id} />
              {entry.sibling_live_url && (
                <a
                  href={entry.sibling_live_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bl-gallery-detail__sibling"
                  title="The maker shipped a live mini-app for this project — open it in a new tab"
                >
                  🚀 Try the live app
                </a>
              )}
              {!entry.is_mine && (
                <button
                  type="button"
                  className="bl-gallery-detail__report"
                  onClick={() => setReporting(true)}
                >
                  Report
                </button>
              )}
            </div>
          </section>

          <footer className="bl-gallery-detail__meta">
            <dl>
              <dt>Shared</dt>
              <dd>{new Date(entry.submitted_at).toLocaleString()}</dd>
              <dt>Visibility</dt>
              <dd>Public</dd>
              <dt>Remixes</dt>
              <dd>
                {entry.remix_count}
                {entry.remix_count === 1 ? ' time' : ' times'}
              </dd>
            </dl>
          </footer>

          {entry.is_mine && <MakerControls entry={entry} />}
        </div>
      </article>

      {reporting && (
        <ReportDialog
          isOpen={true}
          submissionId={entry.id}
          projectName={entry.project_name}
          onClose={() => setReporting(false)}
        />
      )}
    </PageShell>
  );
}

/**
 * Page shell: top brand chrome + centered content + footer. Used for
 * every state of this page (loading, error, missing, full detail) so
 * the visitor always sees BytaApps branding, never a bare alert.
 */
function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bl-page">
      <AppHeader
        subBrand="Gallery"
        privacy="published"
        back={{ label: 'Back to gallery', to: '/gallery' }}
      />
      <main className="bl-gallery-detail">{children}</main>
      <AppFooter />
    </div>
  );
}

/**
 * Description block on the detail page. Renders the maker's text +
 * (when the viewer is the maker) an inline edit affordance: tap
 * "Edit description" → textarea + counter + Save / Cancel.
 *
 * The "no description yet" placeholder gets a softer call-to-action
 * for the maker ("Add one — say what your model does") instead of
 * the read-only message visitors see.
 */
function DescriptionPanel({ entry }: { entry: GalleryEntry }): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.description ?? '');
  const update = useUpdateMyDescription();

  // Reset the draft if the underlying entry changes (e.g. another tab
  // saved a new description).
  useEffect(() => {
    setDraft(entry.description ?? '');
    update.reset();
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, entry.description]);

  if (editing) {
    return (
      <form
        className="bl-gallery-detail__desc-form"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(
            { id: entry.id, description: draft },
            { onSuccess: () => setEditing(false) },
          );
        }}
      >
        <label className="bl-gallery-detail__desc-label" htmlFor="bl-detail-desc">
          Tell visitors what this model does
        </label>
        <textarea
          id="bl-detail-desc"
          className="bl-gallery-detail__desc-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 280))}
          rows={3}
          maxLength={280}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- inline edit invoked by an explicit user click; expected focus
          autoFocus
          disabled={update.isPending}
          placeholder="A high-five detector for my brother."
        />
        <p className="bl-gallery-detail__desc-counter" aria-live="polite">
          {draft.length}/280
        </p>
        {update.error && (
          <p className="bl-gallery-detail__desc-error" role="alert">
            {update.error instanceof Error ? update.error.message : 'Could not save.'}
          </p>
        )}
        <div className="bl-gallery-detail__desc-actions">
          <button
            type="submit"
            className="bl-gallery-detail__desc-save"
            disabled={update.isPending || draft.trim() === (entry.description ?? '')}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="bl-gallery-detail__desc-cancel"
            onClick={() => {
              setDraft(entry.description ?? '');
              setEditing(false);
              update.reset();
            }}
            disabled={update.isPending}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  }

  if (entry.description) {
    return (
      <div className="bl-gallery-detail__desc-row">
        <p className="bl-gallery-detail__desc">{entry.description}</p>
        {entry.is_mine && (
          <button
            type="button"
            className="bl-gallery-detail__desc-edit"
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
        )}
      </div>
    );
  }

  if (entry.is_mine) {
    return (
      <p className="bl-gallery-detail__desc bl-gallery-detail__desc--placeholder">
        No description yet.{' '}
        <button
          type="button"
          className="bl-gallery-detail__desc-add"
          onClick={() => setEditing(true)}
        >
          Add one — say what your model does
        </button>
      </p>
    );
  }

  return (
    <p className="bl-gallery-detail__desc bl-gallery-detail__desc--placeholder">
      The maker didn&rsquo;t leave a description for this one.
    </p>
  );
}

/**
 * Maker-only controls block at the bottom of the detail page.
 *
 * Currently just the withdraw flow ("Remove from gallery"), with a
 * two-step inline confirmation matching the unpublish pattern on
 * /studio/me/published. Once confirmed and the server returns the
 * `withdrawn` row, we navigate back to /gallery so the maker doesn't
 * sit on a 404 (the public detail endpoint is approved-only).
 *
 * Analytics: `gallery_submission_withdraw_started` fires the moment
 * the maker reveals the confirm step, `gallery_submission_withdrawn`
 * fires once the server says yes. The server *also* mirrors the
 * latter so the funnel stays sane if the kid closes the tab before
 * navigation completes.
 */
function MakerControls({ entry }: { entry: GalleryEntry }): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const navigate = useNavigate();
  const withdraw = useWithdrawSubmission();

  const onConfirmReveal = (): void => {
    setConfirming(true);
    capture('gallery_submission_withdraw_started', {
      kind: entry.project_kind,
    });
  };

  const onWithdraw = (): void => {
    withdraw.mutate(entry.id, {
      onSuccess: (summary) => {
        capture('gallery_submission_withdrawn', {
          kind: summary.project_kind,
          prior_status: 'approved',
          decision_path: 'user_withdraw',
        });
        navigate('/gallery');
      },
    });
  };

  return (
    <section className="bl-gallery-detail__maker" aria-labelledby="bl-detail-maker-heading">
      <h2 id="bl-detail-maker-heading">Maker controls</h2>
      <p className="bl-gallery-detail__maker-hint">
        This is your project. You can take it down here — it disappears from the gallery and the
        detail page right away. Existing remixes other kids made stay on their devices.
      </p>
      {!confirming ? (
        <button
          type="button"
          className="bl-gallery-detail__maker-withdraw"
          onClick={onConfirmReveal}
          disabled={withdraw.isPending}
        >
          Remove from gallery
        </button>
      ) : (
        <div className="bl-gallery-detail__maker-confirm">
          <span>Take it down?</span>
          <button
            type="button"
            className="bl-gallery-detail__maker-withdraw"
            onClick={onWithdraw}
            disabled={withdraw.isPending}
          >
            {withdraw.isPending ? 'Removing…' : 'Yes, remove it'}
          </button>
          <button
            type="button"
            className="bl-gallery-detail__maker-keep"
            onClick={() => setConfirming(false)}
            disabled={withdraw.isPending}
          >
            Keep it up
          </button>
        </div>
      )}
      {withdraw.error && (
        <p className="bl-gallery-detail__maker-error" role="alert">
          {withdraw.error instanceof Error
            ? withdraw.error.message
            : 'Could not take it down. Try again in a moment.'}
        </p>
      )}
    </section>
  );
}
